/**
 * Contexto Global de Câmeras
 * Gerenciamento centralizado com cache para evitar requisições duplicadas
 */

import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { cameraService, Camera } from '../services/cameraService';
import { authService } from '../services/authService';

interface CamerasContextData {
  cameras: Camera[];
  loading: boolean;
  error: string | null;
  lastFetch: number | null;
  refresh: () => Promise<void>;
  createCamera: (data: Partial<Camera>) => Promise<Camera>;
  updateCamera: (id: string, data: Partial<Camera>) => Promise<Camera>;
  deleteCamera: (id: string) => Promise<void>;
  getCameraById: (id: string) => Camera | undefined;
}

const CamerasContext = createContext<CamerasContextData | undefined>(undefined);

// Cache time: 5 segundos (reduzido para updates mais rápidos)
const CACHE_TIME = 5000;

interface CamerasProviderProps {
  children: ReactNode;
}

export const CamerasProvider: React.FC<CamerasProviderProps> = ({ children }) => {
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetch, setLastFetch] = useState<number | null>(null);
  const [fetchPromise, setFetchPromise] = useState<Promise<void> | null>(null);

  const loadCameras = useCallback(async (force: boolean = false) => {
    if (authService.getUser()?.role === 'partner') {
      setCameras([]);
      setError(null);
      return;
    }
    // Se já está carregando, retorna a promise existente
    if (fetchPromise && !force) {
      return fetchPromise;
    }

    // Se tem cache válido e não é forçado, não faz nada
    const now = Date.now();
    if (!force && lastFetch && (now - lastFetch) < CACHE_TIME) {
      return;
    }

    const promise = (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await cameraService.listCameras();
        setCameras(data);
        setLastFetch(Date.now());
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar câmeras');
        console.error('Erro ao carregar câmeras:', err);
      } finally {
        setLoading(false);
        setFetchPromise(null);
      }
    })();

    setFetchPromise(promise);
    return promise;
  }, [fetchPromise, lastFetch]);

  const refresh = useCallback(async () => {
    await loadCameras(true);
  }, [loadCameras]);

  const createCamera = useCallback(async (data: Partial<Camera>) => {
    try {
      const newCamera = await cameraService.createCamera(data);
      // Forçar refresh completo para garantir dados atualizados
      await loadCameras(true);
      return newCamera;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao criar câmera';
      setError(message);
      throw err;
    }
  }, [loadCameras]);

  const updateCamera = useCallback(async (id: string, data: Partial<Camera>) => {
    try {
      const updated = await cameraService.updateCamera(id, data);
      // Forçar refresh completo para garantir dados atualizados
      await loadCameras(true);
      return updated;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao atualizar câmera';
      setError(message);
      throw err;
    }
  }, [loadCameras]);

  const deleteCamera = useCallback(async (id: string) => {
    try {
      await cameraService.deleteCamera(id);
      setCameras(prev => prev.filter(cam => cam.id !== id));
      setLastFetch(Date.now()); // Atualiza cache
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao deletar câmera';
      setError(message);
      throw err;
    }
  }, []);

  const getCameraById = useCallback((id: string): Camera | undefined => {
    return cameras.find(cam => cam.id === id);
  }, [cameras]);

  // Carrega câmeras na montagem do provider APENAS se houver token
  useEffect(() => {
    const token = localStorage.getItem('awflux_token');
    if (token) {
      loadCameras();
    }
  }, []);

  // Listener para carregar câmeras após login
  useEffect(() => {
    // Listener para evento customizado de login
    const handleLogin = () => {
      loadCameras(true);
    };

    // Listener para mudanças no localStorage (login em outra aba)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'awflux_token' && e.newValue && !e.oldValue) {
        loadCameras(true);
      }
    };

    window.addEventListener('auth:login', handleLogin as EventListener);
    window.addEventListener('storage', handleStorageChange);
    
    // Recarregar câmeras ao trocar de organização (group users)
    const handleOrgSwitch = () => {
      loadCameras(true);
    };
    window.addEventListener('auth:org-switch', handleOrgSwitch as EventListener);
    
    return () => {
      window.removeEventListener('auth:login', handleLogin as EventListener);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth:org-switch', handleOrgSwitch as EventListener);
    };
  }, []);

  return (
    <CamerasContext.Provider
      value={{
        cameras,
        loading,
        error,
        lastFetch,
        refresh,
        createCamera,
        updateCamera,
        deleteCamera,
        getCameraById,
      }}
    >
      {children}
    </CamerasContext.Provider>
  );
};

export const useCamerasContext = (): CamerasContextData => {
  const context = useContext(CamerasContext);
  if (!context) {
    throw new Error('useCamerasContext must be used within CamerasProvider');
  }
  return context;
};
